import { shrinkGifSettings, fitGifSettings, clampGifRange } from "./gifFit";
import { looksLikeVideo } from "./utils";
import { useState, useCallback, useRef } from "react";
import { ToolContainer, ResultPreview, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, stopProcessing, resetFFmpegEngine, videoToGif } from "./operations";
import { BYTES_PER_MB, toMB } from "@/lib/megabytes";
import { VideoProcessingProgress, showVideoError, FileSizeNotice } from "./uiHelpers";
import { AlertTriangle } from "lucide-react";
import type { FFmpeg } from "@ffmpeg/ffmpeg";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Converts a short video clip into an animated GIF, useful for sharing a quick clip somewhere GIFs are supported but video files aren't, like certain chat apps or forum posts. Runs entirely in your browser — the video never leaves your device.`,
  how: `Upload your video and download the resulting animated GIF. Longer clips take more time and produce larger files — this works best on short clips.`,
  tip: `Keep the source clip under about 10 seconds — GIFs don't compress as efficiently as video, so longer clips can produce surprisingly large file sizes and take a while to generate. This is one of the slower video operations here since it has to re-encode every frame into GIF's own format. Some phone-recorded videos (particularly .mov files) use a codec this tool can't read — if processing fails right away, try re-exporting the video from your phone's own share options first.`,
};

// Not a hard limit -- GIF is genuinely unsuited to long clips (huge file,
// slow encode), so this warns clearly rather than silently letting
// someone wait a long time for a GIF they probably don't actually want.
const LONG_VIDEO_WARNING_SECONDS = 20;

// Recommended two-pass approach (generate a color palette first, then use
// it for the actual GIF encode) is the standard way to get GIFs that don't
// look muddy/banded -- a direct one-step GIF encode uses a poor default
// palette and looks noticeably worse.
export default function VideoToGif() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [duration, setDuration] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [start, setStart] = useState("0");
  const [length, setLength] = useState("5");
  const [width, setWidth] = useState("480");
  const [fps, setFps] = useState("12");
  const [speed, setSpeed] = useState("1");
  const [playOnce, setPlayOnce] = useState(false);
  const [maxMb, setMaxMb] = useState("0");
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    if (!looksLikeVideo(f)) { toast.error(`"${f.name}" isn't a video file`); return; }
    setFileError(null);
    setFile(f); setPreview(URL.createObjectURL(f)); setResult(null); setDuration(null);
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) stopProcessing(ffmpegRef.current);
  };

  const convert = async () => {
    if (!file || fileError) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading video engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading video file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp4";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      setStatusMessage("Generating color palette...");
      const { start: st, length: len } = clampGifRange(Number(start), Number(length), duration);
      let w = Number(width), f = Number(fps);
      const limit = Number(maxMb) * BYTES_PER_MB;
      let blob: Blob;
      let fitNote = "";
      if (limit) {
        // Estimate the size from a short sample in the middle of the clip, so the settings are chosen without
        // repeatedly encoding the whole clip.
        const sampleLen = Math.min(len, 1.5);
        const sampleStart = st + Math.max(0, (len - sampleLen) / 2);
        const fit = await fitGifSettings(async (cand) => {
          setStatusMessage(`Estimating the size at ${cand.width} px, ${cand.fps} fps...`);
          const d = await videoToGif(ffmpeg, `input.${ext}`, () => {}, controller.signal, () => {}, { startSeconds: sampleStart, lengthSeconds: sampleLen, width: cand.width, fps: cand.fps, speed: Number(speed), playOnce });
          return d.length;
        }, { width: w, fps: f }, len, sampleLen, limit);
        w = fit.width; f = fit.fps;
        if (!fit.fits) fitNote = `A clip of about ${fit.fitSeconds} s would fit in ${maxMb} MB at ${w} px and ${f} fps.`;
      }
      let tries = 0;
      for (;;) {
        setStatusMessage(`Creating the GIF at ${w} px, ${f} fps...`);
        const data = await videoToGif(ffmpeg, `input.${ext}`, setProgress, controller.signal, () => {}, { startSeconds: st, lengthSeconds: len, width: w, fps: f, speed: Number(speed), playOnce });
        blob = ffmpegOutputToBlob(data, "image/gif");
        if (!limit || blob.size <= limit) break;
        // The estimate was a little low: step down once more and redo.
        const next = shrinkGifSettings(w, f);
        if (!next || ++tries > 2) break;
        w = next.width; f = next.fps;
      }
      setResult(blob);
      const mb = blob.size / BYTES_PER_MB;
      const changed = w !== Number(width) || f !== Number(fps);
      if (limit && blob.size > limit) toast.warning(`GIF is ${mb.toFixed(1)} MB, still over ${maxMb} MB at ${w} px and ${f} fps. Use a shorter clip to make it smaller.`);
      else toast.success(`GIF created: ${mb.toFixed(1)} MB` + (changed ? ` (reduced to ${w} px, ${f} fps to fit ${maxMb} MB)` : "") + (!limit && mb > 8 ? " - large; lower the width or frame rate, or use a shorter clip, to shrink it" : ""));
    } catch (err) {
      showVideoError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="video/*" maxSize={200} onFiles={handleFiles} label="Drop a video to convert to GIF" description="Create animated GIFs from video clips" />
      ) : (
        <div className="space-y-4">
          <video
            src={preview}
            controls
            className="w-full max-h-48 rounded-lg border"
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (!Number.isFinite(d) || d <= 0) { setFileError(`"${file.name}" has no readable video length. Try re-exporting it as MP4.`); return; }
              setDuration(d);
              const c = clampGifRange(Number(start), Number(length), d);
              setStart(String(c.start)); setLength(String(c.length));
            }}
            onError={() => setFileError(`"${file.name}" couldn't be read as a video; it may be damaged or use a codec this browser can't open. Try re-exporting it as MP4.`)}
          />
          {fileError && <p role="alert" className="text-xs text-destructive" data-testid="gif-file-error">{fileError}</p>}
          {duration !== null && !fileError && <p className="text-xs text-muted-foreground" data-testid="gif-duration">Clip is {duration.toFixed(1)} s long. Start and Length are kept inside it.</p>}
          {duration !== null && duration > LONG_VIDEO_WARNING_SECONDS && (
            <NoticeBox className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p className="text-xs">
                This clip is about {Math.round(duration)} seconds long — GIF works best on short clips (under ~10s). Longer source videos take real time to convert and often produce a surprisingly large GIF file. Trim it down first with the Trim Video tool if you only need part of it.
              </p>
            </NoticeBox>
          )}
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><label htmlFor="gif-start" className="text-xs block mb-1">Start (seconds)</label><input id="gif-start" type="number" min="0" step="0.1" className="border rounded px-2 py-1 w-full bg-background" value={start} onChange={(e) => { setStart(e.target.value); setResult(null); }} onBlur={() => { const c = clampGifRange(Number(start), Number(length), duration); setStart(String(c.start)); setLength(String(c.length)); }} /></div>
            <div><label htmlFor="gif-length" className="text-xs block mb-1">Length (seconds)</label><input id="gif-length" type="number" min="0.5" step="0.1" max={duration ? Math.max(0.5, duration - (Number(start) || 0)) : undefined} className="border rounded px-2 py-1 w-full bg-background" value={length} onChange={(e) => { setLength(e.target.value); setResult(null); }} onBlur={() => { const c = clampGifRange(Number(start), Number(length), duration); setStart(String(c.start)); setLength(String(c.length)); }} /></div>
            <div><label className="text-xs block mb-1">Width</label>
              <select className="border rounded px-2 py-1 w-full bg-background" value={width} onChange={(e) => { setWidth(e.target.value); setResult(null); }}>
                {["240", "320", "480", "640", "800"].map((w) => <option key={w} value={w}>{w} px</option>)}
              </select></div>
            <div><label className="text-xs block mb-1">Frames per second</label>
              <select className="border rounded px-2 py-1 w-full bg-background" value={fps} onChange={(e) => { setFps(e.target.value); setResult(null); }}>
                {["5", "8", "10", "12", "15", "20"].map((f) => <option key={f} value={f}>{f} fps</option>)}
              </select></div>
            <div><label className="text-xs block mb-1">Speed</label>
              <select className="border rounded px-2 py-1 w-full bg-background" value={speed} onChange={(e) => { setSpeed(e.target.value); setResult(null); }}>
                {["0.5", "0.75", "1", "1.5", "2"].map((v) => <option key={v} value={v}>{v === "1" ? "Normal speed" : `${v}x`}</option>)}
              </select></div>
            <div className="flex items-end"><label className="flex items-center gap-2 text-sm pb-1"><input type="checkbox" checked={playOnce} onChange={(e) => { setPlayOnce(e.target.checked); setResult(null); }} /> Play once (no loop)</label></div>
            <div><label className="text-xs block mb-1">Largest file</label>
              <select className="border rounded px-2 py-1 w-full bg-background" value={maxMb} onChange={(e) => { setMaxMb(e.target.value); setResult(null); }}>
                <option value="0">No limit</option><option value="2">2 MB</option><option value="5">5 MB</option><option value="8">8 MB (chat apps)</option><option value="10">10 MB</option>
              </select></div>
          </div>
          {result && <p className="text-xs font-medium text-green-600">GIF size: {toMB(result.size, 2)} MB</p>}
          {result && <ResultPreview blob={result} />}
          {processing && <VideoProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={file.name.replace(/\.[^.]+$/, ".gif")} />
                <Button variant="outline" onClick={() => { setFile(null); setPreview(""); setResult(null); setFileError(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={convert} disabled={processing || !!fileError || sizeCheck?.severity !== "ok"}>{processing ? "Creating GIF..." : "Create GIF"}</Button>
                <Button variant="outline" onClick={() => { setFile(null); setPreview(""); setFileError(null); }} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
